import React from 'react';
import {
  CertDayInput,
  CertDayMode,
  certDayBarColor,
  certDayBarWidth,
  certDayPercent,
  certDayTextColor,
  formatCertDays,
  hasCertDaysFor,
} from '../lib/cert-days';

/**
 * The counter-plus-progress-bar block, as four screens draw it.
 *
 * The PHRASING and ABSENT rules live in lib/cert-days.ts and are shared by all
 * six render sites; this component is only the bar layout that four of them
 * happen to have in common. The other two are not bars — a definition row on
 * the equipment detail page and a print field on the inspection record — and
 * they call the lib functions directly rather than being bent into this shape.
 *
 * WHY THE PERCENT IS A PROP RATHER THAN ALWAYS DERIVED. Two of the four
 * payloads carry their own percent (`percentUsed` from /equipment/cert-summary,
 * `percent_used` from /employee/my-equipment/:id) and those must be passed
 * through, not recomputed — the API rounds cert percentages in a service shared
 * with the PDF exports, and a second rounding here would let the screen and the
 * report disagree by a point. Omit the prop only where the payload has none.
 */

const TEXT_SIZE: Record<'sm' | 'xs' | '2xs', string> = {
  sm: 'text-sm',
  xs: 'text-xs',
  '2xs': 'text-[11px]',
};

const BAR_HEIGHT: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'h-1.5',
  md: 'h-2',
  lg: 'h-3',
};

export interface CertDayCounterProps extends CertDayInput {
  /** Always explicit. See the mode discussion in lib/cert-days.ts. */
  mode: CertDayMode;
  /** The API's own percent where the payload carries one. Omit to derive. */
  percentUsed?: number | null;
  size?: keyof typeof TEXT_SIZE;
  barHeight?: keyof typeof BAR_HEIGHT;
  /** Muted label colour for the counter text; defaults to the body colour. */
  muted?: boolean;
  showPercent?: boolean;
  className?: string;
}

export function CertDayCounter({
  daysUsed,
  dayLimit,
  daysRemaining,
  mode,
  percentUsed,
  size = 'sm',
  barHeight = 'lg',
  muted = false,
  showPercent = true,
  className = '',
}: CertDayCounterProps) {
  const input: CertDayInput = { daysUsed, dayLimit, daysRemaining };

  // An absent counter still renders the row — as a dash. Hiding it would make
  // an untracked tool indistinguishable from one with no cert section at all,
  // and the whole point of the dash is that the gap stays visible.
  //
  // Presence is asked in the same MODE the sentence is rendered in, so the
  // percent and the words can never disagree about whether there is a figure.
  const percent = hasCertDaysFor(input, mode)
    ? (percentUsed ?? certDayPercent(input))
    : null;

  return (
    <div className={className}>
      <div className={`flex justify-between items-center gap-2 mb-1.5 ${TEXT_SIZE[size]}`}>
        <span className={muted ? 'text-fieldiq-muted' : 'text-fieldiq-text'}>
          {formatCertDays(input, mode)}
        </span>
        {/* One dash, not two. When there is no figure the sentence on the left
            already says so; a second dash in the percent slot repeats it and
            reads like two separate missing values. */}
        {showPercent && percent !== null && (
          <span className={`font-bold ${certDayTextColor(percent)}`}>{percent}%</span>
        )}
      </div>
      <div className={`w-full ${BAR_HEIGHT[barHeight]} bg-fieldiq-dark rounded-full overflow-hidden`}>
        <div
          data-testid="cert-day-bar"
          className={`h-full rounded-full transition-all ${certDayBarColor(percent)}`}
          style={{ width: certDayBarWidth(percent) }}
        />
      </div>
    </div>
  );
}

export default CertDayCounter;
